import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, X, Circle } from "lucide-react";

const LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function winner(b) {
  for (const [a, c, d] of LINES) {
    if (b[a] && b[a] === b[c] && b[a] === b[d]) return { mark: b[a], line: [a, c, d] };
  }
  return null;
}

export function TicTacToe() {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [turn, setTurn] = useState("X");
  const [scores, setScores] = useState({ X: 0, O: 0, draws: 0 });
  const [result, setResult] = useState(null); // {mark, line} | "draw" | null

  const play = (i) => {
    if (board[i] || result) return;
    const nb = [...board];
    nb[i] = turn;
    setBoard(nb);
    const win = winner(nb);
    if (win) {
      setResult(win);
      setScores((s) => ({ ...s, [win.mark]: s[win.mark] + 1 }));
    } else if (nb.every(Boolean)) {
      setResult("draw");
      setScores((s) => ({ ...s, draws: s.draws + 1 }));
    } else {
      setTurn(turn === "X" ? "O" : "X");
    }
  };

  const nextRound = () => { setBoard(Array(9).fill(null)); setTurn("X"); setResult(null); };
  const resetAll = () => { nextRound(); setScores({ X: 0, O: 0, draws: 0 }); };

  const winLine = result && result !== "draw" ? result.line : [];

  return (
    <div className="flex flex-col items-center w-full" data-testid="tictactoe-game">
      <div className="grid grid-cols-3 gap-3 w-full max-w-md mb-6 text-center">
        <ScoreBox label="Player X" color="#00F0FF" value={scores.X} active={turn === "X" && !result} />
        <ScoreBox label="Draws" color="#71717a" value={scores.draws} />
        <ScoreBox label="Player O" color="#FF003C" value={scores.O} active={turn === "O" && !result} />
      </div>

      <div className="relative grid grid-cols-3 gap-2 p-2 bg-zinc-900 rounded-xl border-2 border-zinc-700" style={{ width: "min(86vw, 320px)" }}>
        {board.map((cell, i) => (
          <button key={i} onClick={() => play(i)} data-testid={`ttt-cell-${i}`}
            className="aspect-square rounded-lg bg-zinc-950 border-2 flex items-center justify-center transition-all hover:bg-zinc-800"
            style={{ borderColor: winLine.includes(i) ? "#39FF14" : "#27272a", boxShadow: winLine.includes(i) ? "0 0 14px #39FF14" : "none" }}>
            {cell === "X" && <X className="w-12 h-12 text-neon-cyan" strokeWidth={3} />}
            {cell === "O" && <Circle className="w-10 h-10 text-neon-pink" strokeWidth={3} />}
          </button>
        ))}
      </div>

      <div className="h-16 flex flex-col items-center justify-center mt-5">
        {result ? (
          <>
            <p className="font-display font-black text-xl uppercase mb-2" data-testid="ttt-result">
              {result === "draw" ? "It's a draw!" : (
                <span style={{ color: result.mark === "X" ? "#00F0FF" : "#FF003C" }}>Player {result.mark} wins! 🏆</span>
              )}
            </p>
            <div className="flex gap-3">
              <Button onClick={nextRound} data-testid="ttt-next" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-9 text-xs">Next Round</Button>
              <Button onClick={resetAll} variant="outline" className="border-zinc-700 text-white rounded-none font-bold uppercase h-9 text-xs">
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
            </div>
          </>
        ) : (
          <p className="font-mono text-zinc-400">
            Turn: <span className="font-bold" style={{ color: turn === "X" ? "#00F0FF" : "#FF003C" }}>Player {turn}</span>
          </p>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono">Two players, one screen. Take turns tapping a square.</p>
    </div>
  );
}

function ScoreBox({ label, color, value, active }) {
  return (
    <div className="rounded-lg border-2 py-2 transition-all" style={{ borderColor: active ? color : "#27272a", background: active ? `${color}15` : "transparent" }}>
      <p className="font-display font-bold uppercase text-xs" style={{ color }}>{label}</p>
      <p className="font-display font-black text-2xl" style={{ color }}>{value}</p>
    </div>
  );
}
